import React from 'react';
import { AlertTriangle, Cpu, Download, HardDriveDownload, RefreshCw, ScrollText, Trash2, UploadCloud } from 'lucide-react';
import { PanelSection } from '../ui/PanelSection';
import { Chip } from '../ui/Chip';
import { EmptyNote } from '../ui/EmptyNote';
import { Field } from '../ui/Field';
import { NumberStepper } from '../ui/NumberStepper';
import { TypedConfirmDialog } from '../ui/TypedConfirmDialog';
import { useRcuLink } from '../useRcuLink';
import { TONE } from '../../../lib/rcu/catalog/feedback';
import { ACK_STATUS, COREDUMP_RESET_LABEL, errorLabel } from '../../../lib/rcu/catalog/ack';
import {
  COREDUMP_CHUNK,
  LOG_DROP_NOTICE_PATTERN,
  LOG_STREAM,
  S3_IMPORT_MAX_BYTES,
} from '../../../lib/rcu/catalog/diagnostics';
import { RCU_OTA, RCU_S3_IMPORT_MAX_BYTES } from '../../../lib/rcu/catalog/protocol';
import { LINK_LIMITS, MEMORY_PRESSURE, toggleWord } from '../../../lib/rcu/catalog/link';
import {
  downloadCoredump,
  eraseCoredump,
  importS3Mapping,
  otaUrl,
  readCoredumpInfo,
  readLoggingStatus,
  readLogs,
  setLogging,
  startOta,
} from '../../../lib/rcu/link/commands';
import { assembleCoredump, coredumpFileName, formatBytes, progressOf } from '../../../lib/rcu/link/coredump';
import { saveBytes } from '../../../lib/file';
import { useRcuConfigStore } from '../../../stores/rcuConfigStore';
import { useRcuLinkStore } from '../../../stores/rcuLinkStore';

const LoggingCard: React.FC = () => {
  const live = useRcuLinkStore((state) => state.live);
  const { send, inFlight } = useRcuLink();
  const enabled = live.logging?.enabled ?? false;
  const streaming = live.logging?.mqtt ?? false;

  return (
    <PanelSection
      title="Logging"
      description="The RCU keeps 128 lines in RAM and can mirror them to the broker as they happen."
      action={
        <button
          type="button"
          disabled={inFlight > 0}
          onClick={() => void send(readLoggingStatus(), 'Logging status')}
          className="flex shrink-0 items-center gap-2 rounded-full border border-[#E0E0E0] px-4 py-2 text-sm font-medium text-[#666666] transition-colors hover:border-[#D4A054] hover:text-[#333333] disabled:opacity-50"
        >
          <RefreshCw className="h-4 w-4" />
          Read state
        </button>
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <Chip
          label={live.logging === null ? 'not read' : `console ${toggleWord(enabled)}`}
          tone={live.logging === null ? TONE.NEUTRAL : enabled ? TONE.SUCCESS : TONE.NEUTRAL}
        />
        <Chip
          label={live.logging === null ? 'not read' : `MQTT ${toggleWord(streaming)}`}
          tone={live.logging === null ? TONE.NEUTRAL : streaming ? TONE.SUCCESS : TONE.NEUTRAL}
        />
        {live.loggingAck !== null && (
          <span className="text-xs text-[#666666]">
            Last change: {live.loggingAck.status}
            {live.loggingAck.mqtt === null ? '' : ` · MQTT ${toggleWord(live.loggingAck.mqtt)}`}
          </span>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={inFlight > 0}
          onClick={() => void send(setLogging({ enable: true, mqtt: true }), 'Logging enabled')}
          className="rounded-full bg-[#1A1A1A] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#333333] disabled:opacity-50"
        >
          Stream to the broker
        </button>
        <button
          type="button"
          disabled={inFlight > 0}
          onClick={() => void send(setLogging({ enable: true }), 'Logging enabled')}
          className="rounded-full border border-[#E0E0E0] px-4 py-2 text-sm font-medium text-[#666666] transition-colors hover:border-[#D4A054] hover:text-[#333333] disabled:opacity-50"
        >
          Enable, leave MQTT as is
        </button>
        <button
          type="button"
          disabled={inFlight > 0}
          onClick={() => void send(setLogging({ enable: false }), 'Logging disabled')}
          className="rounded-full border border-[#E0E0E0] px-4 py-2 text-sm font-medium text-[#666666] transition-colors hover:border-[#F44336] hover:text-[#C62828] disabled:opacity-50"
        >
          Disable
        </button>
      </div>

      <p className="mt-3 text-xs text-[#666666]">
        An accepted change is not proof that the MQTT setting was stored: the reply’s `status` is
        written before the setting is saved. `loggingstatus.mqtt` is the reliable one; `enabled` is
        reported from a variable that is not always current.
      </p>
    </PanelSection>
  );
};

const LogTail: React.FC = () => {
  const logLines = useRcuLinkStore((state) => state.logLines);
  const droppedLogLines = useRcuLinkStore((state) => state.droppedLogLines);
  const live = useRcuLinkStore((state) => state.live);
  const { send, inFlight } = useRcuLink();
  const [lines, setLines] = React.useState<number>(LOG_STREAM.DEFAULT_LINES);

  return (
    <PanelSection
      title="Log tail"
      description={`The device keeps ${LOG_STREAM.RAM_LINES} lines of ${LOG_STREAM.LINE_BYTES} bytes and sends them in batches of about ${LOG_STREAM.BATCH_BYTES} bytes.`}
      action={
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            disabled={inFlight > 0}
            onClick={() => void send(readLogs(lines), 'Log fetch')}
            className="flex items-center gap-2 rounded-full bg-[#1A1A1A] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#333333] disabled:opacity-50"
          >
            <ScrollText className="h-4 w-4" />
            Fetch
          </button>
        </div>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <NumberStepper
          label="Lines to fetch"
          value={lines}
          min={LOG_STREAM.MIN_LINES}
          max={LOG_STREAM.MAX_LINES}
          onChange={setLines}
          hint={`Anything larger is capped at ${LOG_STREAM.MAX_LINES} by the firmware.`}
        />
        <div className="rounded-xl bg-[#F5F5F5] px-3 py-2 text-xs text-[#666666]">
          <p>
            Kept in this console: {logLines.length} of {LINK_LIMITS.LOG_RING} lines
            {droppedLogLines > 0 ? ` · ${droppedLogLines} dropped` : ''}.
          </p>
          {live.logs !== null && (
            <p className="mt-1">
              The device said {live.logs.pending ?? 0} lines were still pending when the fetch was
              accepted — that is not a completion signal.
            </p>
          )}
        </div>
      </div>

      {logLines.length === 0 ? (
        <div className="mt-4">
          <EmptyNote
            message="Nothing yet. Enable MQTT streaming for live lines, or fetch the buffer the device holds."
            action={
              <button
                type="button"
                onClick={() => void send(readLogs(lines), 'Log fetch')}
                className="rounded-full border border-[#E0E0E0] px-4 py-2 text-sm font-medium text-[#666666] transition-colors hover:border-[#D4A054]"
              >
                Fetch logs
              </button>
            }
          />
        </div>
      ) : (
        <div className="mt-4 max-h-80 overflow-y-auto rounded-2xl bg-[#1A1A1A] p-3">
          <ul className="space-y-0.5 font-mono text-[11px] leading-relaxed text-white/80">
            {logLines
              .slice()
              .reverse()
              .map((line, index) => (
                <li
                  key={`${line.at}-${index}`}
                  className={LOG_DROP_NOTICE_PATTERN.test(line.text) ? 'text-[#FFB74D]' : ''}
                >
                  <span className="mr-2 text-white/35">{new Date(line.at).toLocaleTimeString()}</span>
                  {line.text}
                </li>
              ))}
          </ul>
        </div>
      )}

      <p className="mt-3 text-xs text-[#666666]">
        Lines the device had to drop arrive as their own notice, and the console counts them
        separately from the ones it keeps.
      </p>
    </PanelSection>
  );
};

const SALVAGE_NOTE = 'Chunks may repeat over QoS 1; the console deduplicates them by dump id, upload and sequence.';

const CoredumpCard: React.FC = () => {
  const config = useRcuConfigStore((state) => state.config);
  const live = useRcuLinkStore((state) => state.live);
  const { send, inFlight } = useRcuLink();
  const [confirming, setConfirming] = React.useState(false);

  const transfer = live.coredumpTransfer;
  const progress = transfer === null ? null : progressOf(transfer);
  const bytes = transfer === null ? null : assembleCoredump(transfer);
  const serial = config.rcu.serialNumber.trim();

  return (
    <PanelSection
      title="Coredump"
      description="A crash image, streamed in base64 chunks over MQTT and reassembled here."
      action={
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            disabled={inFlight > 0}
            onClick={() => void send(readCoredumpInfo(), 'Coredump info')}
            className="flex items-center gap-2 rounded-full border border-[#E0E0E0] px-4 py-2 text-sm font-medium text-[#666666] transition-colors hover:border-[#D4A054] hover:text-[#333333] disabled:opacity-50"
          >
            <HardDriveDownload className="h-4 w-4" />
            Read info
          </button>
          <button
            type="button"
            disabled={inFlight > 0 || live.coredump?.present !== true}
            onClick={() => void send(downloadCoredump(), 'Coredump download')}
            className="flex items-center gap-2 rounded-full bg-[#1A1A1A] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#333333] disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            Download
          </button>
        </div>
      }
    >
      {live.coredump === null ? (
        <EmptyNote message="No coredump information read yet." />
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Chip
              label={live.coredump.present ? 'stored' : 'none stored'}
              tone={live.coredump.present ? TONE.WARNING : TONE.NEUTRAL}
            />
            <span className="text-sm text-[#666666]">
              Reset reason {COREDUMP_RESET_LABEL[live.coredump.reset]} · crash sequence{' '}
              {live.coredump.crashSeq}
            </span>
          </div>

          {live.coredump.present && (
            <div className="grid gap-2 text-sm text-[#666666] sm:grid-cols-2 lg:grid-cols-4">
              <p className="font-mono">id {live.coredump.dumpId}</p>
              <p>{formatBytes(live.coredump.size)}</p>
              <p>task {live.coredump.task === '' ? '—' : live.coredump.task}</p>
              <p className="font-mono">pc {live.coredump.pc === '' ? '—' : live.coredump.pc}</p>
            </div>
          )}

          {progress !== null && (
            <div className="rounded-2xl bg-[#F5F5F5] px-3 py-3">
              <div className="flex items-center justify-between text-sm text-[#333333]">
                <span>
                  Received {progress.received} of {progress.total} chunks
                </span>
                <span className="font-mono text-xs text-[#666666]">
                  {formatBytes(progress.receivedBytes)} / {formatBytes(progress.expectedBytes)}
                </span>
              </div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#E0E0E0]">
                <div
                  className={`h-full rounded-full ${progress.complete ? 'bg-[#4CAF50]' : 'bg-[#D4A054]'}`}
                  style={{
                    width: `${Math.min(
                      MEMORY_PRESSURE.FULL_PERCENT,
                      progress.total === 0
                        ? 0
                        : (progress.received / progress.total) * MEMORY_PRESSURE.FULL_PERCENT,
                    )}%`,
                  }}
                />
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  disabled={bytes === null}
                  onClick={() => {
                    if (bytes !== null && transfer !== null) {
                      saveBytes(bytes, coredumpFileName(serial, transfer));
                    }
                  }}
                  className="rounded-full border border-[#E0E0E0] bg-white px-3 py-1.5 text-xs font-medium text-[#666666] transition-colors hover:border-[#D4A054] disabled:opacity-50"
                >
                  Save {transfer === null ? 'file' : coredumpFileName(serial, transfer)}
                </button>
                <span className="text-xs text-[#666666]">
                  {progress.complete
                    ? 'Image complete.'
                    : `Waiting for ${progress.total - progress.received} more chunk(s).`}
                </span>
              </div>
            </div>
          )}

          {live.coredumpAck !== null && (
            <p className="text-sm text-[#666666]">
              {live.coredumpAck.error === ''
                ? live.coredumpAck.erased
                  ? 'The stored image was erased.'
                  : `Upload reported ${live.coredumpAck.status} with ${live.coredumpAck.chunks ?? 0} chunks planned.`
                : `Refused: ${errorLabel(live.coredumpAck.error)}`}
            </p>
          )}

          <p className="text-xs text-[#666666]">{SALVAGE_NOTE}</p>

          <button
            type="button"
            disabled={inFlight > 0 || live.coredump.present !== true}
            onClick={() => setConfirming(true)}
            className="flex items-center gap-2 rounded-full border border-[#E0E0E0] px-4 py-2 text-sm font-medium text-[#666666] transition-colors hover:border-[#F44336] hover:text-[#C62828] disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" />
            Erase stored image
          </button>
        </div>
      )}

      <TypedConfirmDialog
        isOpen={confirming}
        title="Erase the stored coredump"
        message="The image cannot be recovered afterwards. Download it first if it has not been analysed."
        expected={serial === '' ? 'the RCU serial number' : serial}
        confirmLabel="Erase"
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          void send(eraseCoredump(), 'Coredump erased');
        }}
      />
    </PanelSection>
  );
};

const FirmwareCard: React.FC = () => {
  const live = useRcuLinkStore((state) => state.live);
  const { send, inFlight } = useRcuLink();
  const [version, setVersion] = React.useState('');
  const [confirming, setConfirming] = React.useState(false);
  const config = useRcuConfigStore((state) => state.config);
  const serial = config.rcu.serialNumber.trim();

  const effective = version.trim() === '' ? (live.status?.firmware?.version ?? '') : version.trim();

  return (
    <PanelSection
      title="Firmware update"
      description="The device compiles its own download URL from the version you give it."
    >
      <Field
        label="Version"
        value={version}
        onChange={setVersion}
        placeholder={live.status?.firmware?.version ?? '4.2.1'}
        hint="The RCU fetches this build from its own firmware bucket; the URL is compiled in, not sent."
      />
      <p className="mt-2 font-mono text-xs text-[#666666]">
        {effective === '' ? 'Enter a version to see the URL.' : otaUrl(effective)}
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={inFlight > 0 || effective === ''}
          onClick={() => setConfirming(true)}
          className="flex items-center gap-2 rounded-full bg-[#1A1A1A] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#333333] disabled:opacity-50"
        >
          <UploadCloud className="h-4 w-4" />
          Start update
        </button>
        <Chip
          label={live.ota.status === null ? `${live.ota.progress}%` : live.ota.status}
          tone={
            live.ota.status === null
              ? TONE.INFO
              : live.ota.status === ACK_STATUS.OK
                ? TONE.SUCCESS
                : TONE.ERROR
          }
        />
      </div>

      {live.ota.progress > 0 && live.ota.status === null && (
        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-[#E0E0E0]">
          <div className="h-full rounded-full bg-[#D4A054]" style={{ width: `${live.ota.progress}%` }} />
        </div>
      )}

      <p className="mt-3 flex gap-2 text-xs text-[#666666]">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#FF9800]" />
        <span>
          Progress arrives at new multiples of {RCU_OTA.PROGRESS_STEP}% and{' '}
          {RCU_OTA.FULL_PERCENT}% is not guaranteed; the device reboots when the update is applied,
          and the completion reply is the only signal that it worked. Several builds report the same
          version, so confirm the build date afterwards.
        </span>
      </p>

      <TypedConfirmDialog
        isOpen={confirming}
        title="Start a firmware update"
        message={`The device will download and install ${effective}. It reboots while applying, and the console loses its session until it is back.`}
        expected={serial === '' ? 'the RCU serial number' : serial}
        confirmLabel="Start update"
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          void send(startOta(effective), 'Firmware update');
        }}
      />
    </PanelSection>
  );
};

const ImportCard: React.FC = () => {
  const live = useRcuLinkStore((state) => state.live);
  const config = useRcuConfigStore((state) => state.config);
  const { send, inFlight } = useRcuLink();
  const [url, setUrl] = React.useState('');
  const [confirming, setConfirming] = React.useState(false);
  const serial = config.rcu.serialNumber.trim();
  const ack = live.s3.ack;

  return (
    <PanelSection
      title="Import mappings"
      description="The device fetches a JSON file and replaces its mapping tables with it."
    >
      <Field
        label="File URL"
        value={url}
        onChange={setUrl}
        placeholder="https://example.test/rcu/mappings.json"
        hint={`Must be under ${S3_IMPORT_MAX_BYTES} bytes (the device's limit is ${RCU_S3_IMPORT_MAX_BYTES}). The import is not transactional: a failure part-way leaves what it already wrote.`}
      />

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={inFlight > 0 || url.trim() === ''}
          onClick={() => setConfirming(true)}
          className="flex items-center gap-2 rounded-full bg-[#1A1A1A] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#333333] disabled:opacity-50"
        >
          <Cpu className="h-4 w-4" />
          Import
        </button>
        <Chip
          label={ack === null ? `${live.s3.progress}%` : ack.error === '' ? ack.status : errorLabel(ack.error)}
          tone={
            ack === null
              ? TONE.INFO
              : ack.error === ''
                ? ack.status === ACK_STATUS.OK
                  ? TONE.SUCCESS
                  : TONE.ERROR
                : TONE.ERROR
          }
        />
      </div>

      {ack !== null && ack.error === '' && (
        <p className="mt-3 text-sm text-[#666666]">
          Imported {ack.scenes ?? 0} scenes, {ack.led ?? 0} LED mappings and {ack.dali ?? 0} DALI
          mappings.
        </p>
      )}

      <TypedConfirmDialog
        isOpen={confirming}
        title="Import mappings"
        message={`The device will fetch ${url.trim()} and replace its mapping tables. Anything stored there now is overwritten.`}
        expected={serial === '' ? 'the RCU serial number' : serial}
        confirmLabel="Import"
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          void send(importS3Mapping(url.trim()), 'Mapping import');
        }}
      />
    </PanelSection>
  );
};

/** The limits behind these controls, with the one constant that varies. */
const DIAGNOSTIC_NOTES = [
  'A log fetch is accepted before the lines are sent: the receipt carries a pending count, not a completion.',
  `Batches arrive at least ${LOG_STREAM.BATCH_INTERVAL_MS} ms apart and never exceed ${LOG_STREAM.BATCH_MAX_BYTES} bytes each.`,
  `A coredump chunk carries at most ${COREDUMP_CHUNK.RAW_BYTES} raw bytes, and its size field is the whole image, not the chunk.`,
  'OTA progress can stop short of 100% even on success, because only new multiples of ten are reported.',
  `The mapping import is not transactional, and the file must be under ${S3_IMPORT_MAX_BYTES} bytes.`,
];

/**
 * Everything a technician needs when the device misbehaves: its logs, its crash
 * images, its firmware and its mapping import, each with the limits the firmware
 * actually applies stated next to the control.
 */
export const DiagnosticsPanel: React.FC = () => (
  <div className="space-y-5">
    <LoggingCard />
    <LogTail />
    <CoredumpCard />
    <FirmwareCard />
    <ImportCard />
    <PanelSection
      title="Reading these diagnostics"
      description="What each reply does and does not prove."
    >
      <ul className="space-y-1.5 text-sm text-[#666666]">
        {DIAGNOSTIC_NOTES.map((line) => (
          <li key={line} className="flex gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#D4A054]" />
            <span>{line}</span>
          </li>
        ))}
      </ul>
    </PanelSection>
  </div>
);

export default DiagnosticsPanel;
